import classNames from 'classnames';
import { IS_RECURRING_FIELD } from 'constants/fields';
import type { Currency } from 'models/currency';
import type { Field } from 'models/field';
import type { SingleOffer } from 'models/offer';
import type { Price } from 'models/price';
import { useSelector } from 'react-redux';
import type { RootState } from 'reducers/types';
import { getFieldValue } from 'selectors/ui/ui';
import { Option } from '../Option/Option';

type OptionType<O extends SingleOffer> = O & {
  fee: {
    value: number;
    currency: string | Currency;
  };
  price: Price<string>;
  interval?: number;
};

export function OptionList<O extends SingleOffer>({
  dirtyFields,
  errors,
  isFetchingOffer,
  options,
  values,
  onChange,
  onFieldChange,
  type = 'single',
}: {
  dirtyFields: Record<string, boolean>;
  errors: Record<string, string[]>;
  isFetchingOffer: boolean;
  options: OptionType<O>[];
  values: Record<string, string>;
  onChange: (id: string) => void;
  onFieldChange: (name: string, value: string) => void;
  type?: 'single' | 'recurring';
}) {
  const isRecurring = useSelector((state: RootState) =>
    getFieldValue(state, IS_RECURRING_FIELD),
  );

  return (
    <>
      {options.map(option => {
        const { fields, ...rest } = option;
        const addError = (field: Field) => ({
          ...field,
          error:
            field.error ||
            (dirtyFields[field.id] &&
              errors[field.id] &&
              errors[field.id][0]) ||
            undefined,
        });
        const fieldsWithErrors = fields.map(addError);
        const isSelected =
          values?.offer === option.id &&
          (isRecurring ? type === 'recurring' : type === 'single');
        const areFieldsVisible =
          !isFetchingOffer && isSelected && fields.length > 0;

        return (
          <Option
            key={option.id}
            selected={isSelected}
            onChange={() => onChange(option.id)}
            {...rest}
          >
            <div
              className={classNames('PaymentOptions-fields-container', {
                expanded: areFieldsVisible,
              })}
            >
              {isSelected && (
                <Option.Fields
                  fields={fieldsWithErrors}
                  onChange={onFieldChange}
                />
              )}
            </div>
          </Option>
        );
      })}
    </>
  );
}
